import { HelpText } from '@ovComponents/2-component/helpText/helpText';
import { Radio } from '@ovComponents/2-component/radio/radio';
import { Box, Typography, useTheme } from 'hephaestus/web';
import { kebabCase } from 'lodash-es';
import { useSelectionTileTokens } from './selectionTile.tokens';

export type SelectionTileOption = {
  label: string;
  subtitle?: string;
  value: string | number;
  labelSideComponent?: React.ReactNode;
  disabled?: boolean;
  helpText?: string;
};

type SelectionTileRadioOptionProps = {
  option: SelectionTileOption;
  index: number;
  testId?: string;
  direction: 'row' | 'column';
  xs?: number;
  groupDisabled: boolean;
  changing: boolean;
  helpTextId: string;
  onSelect: (value: string | number) => void;
};

const getFlex = (direction: 'row' | 'column', xs?: number) => {
  if (direction !== 'row') return undefined;
  return xs ? `1 1 ${(xs / 12) * 100}%` : '1 1 0';
};

export const SelectionTileRadioOption = ({
  option,
  index,
  testId,
  direction,
  xs,
  groupDisabled,
  changing,
  helpTextId,
  onSelect,
}: SelectionTileRadioOptionProps) => {
  const theme = useTheme();
  const tokens = useSelectionTileTokens(theme);
  const optionTestId = testId
    ? `${testId}-${kebabCase(option.label)}`
    : undefined;

  const boxStyle = {
    border: `1px solid ${tokens.borderColor}`,
    borderRadius: tokens.borderRadius,
    px: '12px',
    py: '2px',
    cursor: option.disabled ? 'default' : 'pointer',
    transition: 'all 0.2s ease-in-out',
    '&:hover': option.disabled
      ? {}
      : {
          borderColor: tokens.hoverBorderColor,
          backgroundColor: tokens.hoverBackgroundColor,
        },
  };

  return (
    <Box
      data-testid={optionTestId ?? `option-${index}`}
      sx={{
        flex: getFlex(direction, xs),
        width: direction === 'column' ? '100%' : undefined,
        minWidth: 0,
      }}
    >
      <Box
        sx={boxStyle}
        onClick={() => {
          if (groupDisabled || changing || option.disabled) return;
          onSelect(option.value);
        }}
      >
        <Radio
          testId={optionTestId ? `${optionTestId}-radio` : `radio-${index}`}
          disabled={groupDisabled || option.disabled}
          describedBy={option.helpText ? helpTextId : undefined}
          value={option.value}
          label={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {option.label}
              {option.labelSideComponent}
            </Box>
          }
          size='small'
        />
        {option.subtitle && (
          <Typography
            component='p'
            data-testid='subtitle'
            variant='bodySmall'
            color='textSecondary'
            sx={{
              ml: '28px',
              mb: '6px',
              mt: -1,
              pb: 1,
            }}
          >
            {option.subtitle}
          </Typography>
        )}
        {option.helpText && (
          <Box id={helpTextId} sx={{ ml: '28px', pb: 1 }}>
            <HelpText text={option.helpText} />
          </Box>
        )}
      </Box>
    </Box>
  );
};
